import { Container, Graphics } from "pixi.js";

/**
 * Where the passage being worked on begins and ends, drawn on the roll.
 *
 * The steps that play a range ask the player to *go to the end of it*, and
 * until now nothing on screen said where that end was: the roll shows the
 * whole piece, the same notes before and after, with no seam. A player told
 * « va au bout » was being sent to a place that had no marker.
 *
 * Two channels, and neither is a colour that means something else:
 *
 *  - **the outside is dimmed**, the same idiom the past overlay uses for what
 *    has already gone by, so "not now" reads the same way twice;
 *  - **the two boundaries are ruled**, brighter than a bar line, because they
 *    are the one thing on the roll the player has to find.
 *
 * Achromatic on purpose. The two hands own the only hues on this surface, and
 * a tinted boundary would read as one of them.
 */

type WorkRangeProps = {
  /** `null` clears the layer: no range is being worked on. */
  readonly range: { readonly startTick: number; readonly endTick: number } | null;
  /**
   * Faut-il régler les deux bornes, ou seulement assombrir l'extérieur ?
   *
   * La zone d'enregistrement emprunte ce calque pour son voile, mais trace ses
   * propres bornes — en violet, avec leurs capsules et leurs noms (voir
   * `RecordMarker`). Les régler ici en blanc les doublerait d'un trait qui dit
   * la même chose sans le dire pareil.
   */
  readonly ruled: boolean;
  readonly fromTick: number;
  readonly toTick: number;
  readonly width: number;
  readonly height: number;
};

/**
 * How dark the outside gets.
 *
 * Enough to read as another region, not enough to hide the notes: what comes
 * before and after the unit is the context the player is placing it in, and
 * blacking it out would make the unit float in nothing.
 */
const OUTSIDE_ALPHA = 0.42;

/**
 * Contrast of a boundary against the roll (#0a0a10).
 *
 * 0.62 puts it near 6:1 — well past the 3:1 WCAG 1.4.11 asks of a meaningful
 * graphic, and deliberately above the 0.4 of a bar line: a bar is a landmark
 * inside the passage, these are the passage.
 */
const EDGE_ALPHA = 0.62;
const EDGE_WIDTH = 2;

export class WorkRange {
  private container: Container;
  private scrim: Graphics;
  private edges: Graphics;

  private previousKey = "";

  constructor(stage: Container) {
    this.container = new Container();
    this.scrim = new Graphics();
    this.edges = new Graphics();
    this.container.addChild(this.scrim);
    this.container.addChild(this.edges);
    stage.addChild(this.container);
  }

  draw(props: WorkRangeProps): void {
    const { range, fromTick, toTick, width, height } = props;
    const span = toTick - fromTick;

    if (range === null || span <= 0 || width <= 0 || height <= 0) {
      this.container.visible = false;
      this.previousKey = "";

      return;
    }

    const key = `${range.startTick}:${range.endTick}:${fromTick}:${toTick}:${width}:${height}:${props.ruled}`;

    this.container.visible = true;

    if (key === this.previousKey) {
      return;
    }

    this.previousKey = key;

    // The roll falls: a tick further in the future sits higher up.
    const yOf = (tick: number) => height - ((tick - fromTick) / span) * height;
    const yStart = yOf(range.startTick);
    const yEnd = yOf(range.endTick);

    this.scrim.clear();

    // Below the start of the range: already behind the passage.
    if (yStart < height) {
      this.scrim.rect(0, yStart, width, height - yStart);
    }

    // Above the end of the range: not yet the passage.
    if (yEnd > 0) {
      this.scrim.rect(0, 0, width, yEnd);
    }

    this.scrim.fill({ color: 0x000000, alpha: OUTSIDE_ALPHA });

    this.edges.clear();

    if (!props.ruled) {
      return;
    }

    for (const y of [yStart, yEnd]) {
      if (y < -EDGE_WIDTH || y > height + EDGE_WIDTH) {
        continue;
      }

      this.edges.moveTo(0, y);
      this.edges.lineTo(width, y);
    }

    this.edges.stroke({ color: 0xffffff, alpha: EDGE_ALPHA, width: EDGE_WIDTH });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
